import React, { useState, useRef, useEffect } from "react";
import { AnalysisType, analysisTypes } from "./DecisionSupportSidebar";
import * as decisionSupportService from "../services/decisionSupport";
import "./NewSessionModal.css";
import "./InputPanel.css"; // Import for btn-primary and btn-secondary styles

interface NewSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSession: (
    title: string,
    analysisType: AnalysisType,
    startingPrompt: string,
    files: File[],
    ragEnabled: boolean,
    ragNumChunks: number,
    ragMinScore: number
  ) => Promise<void>;
}

const NewSessionModal: React.FC<NewSessionModalProps> = ({
  isOpen,
  onClose,
  onCreateSession,
}) => {
  const [selectedType, setSelectedType] = useState<AnalysisType>(
    AnalysisType.CLINICAL_DECISION_SUPPORT
  );
  const [title, setTitle] = useState<string>("");
  const [startingPrompt, setStartingPrompt] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [promptsMap, setPromptsMap] = useState<Record<string, string>>({});
  const [ragEnabled, setRagEnabled] = useState(true);
  const [ragNumChunks, setRagNumChunks] = useState(5);
  const [ragMinScore, setRagMinScore] = useState(0.3);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch prompts from backend when modal opens
  useEffect(() => {
    if (isOpen && Object.keys(promptsMap).length === 0) {
      const fetchPrompts = async () => {
        setIsLoadingPrompts(true);
        try {
          const prompts = await decisionSupportService.getAllPrompts();
          const map: Record<string, string> = {};
          prompts.forEach((p) => {
            map[p.analysis_type] = p.prompt;
          });
          setPromptsMap(map);

          // Set initial prompt for default analysis type
          setStartingPrompt(map[AnalysisType.CLINICAL_DECISION_SUPPORT] || "");

          // Set initial title
          const typeInfo = analysisTypes.find(
            (t) => t.id === AnalysisType.CLINICAL_DECISION_SUPPORT
          );
          setTitle(typeInfo?.name || "New Session");
        } catch (err) {
          console.error("Error fetching prompts:", err);
          setError("Failed to load prompts from server. Please try again.");
        } finally {
          setIsLoadingPrompts(false);
        }
      };

      fetchPrompts();
    }
  }, [isOpen, promptsMap]);

  // Update starting prompt when analysis type changes
  const handleTypeChange = (type: AnalysisType) => {
    setSelectedType(type);

    // Get the prompt for this type from the fetched prompts
    const prompt = promptsMap[type] || "";
    setStartingPrompt(prompt);

    // Auto-generate title if not manually edited
    if (!title || analysisTypes.some((t) => title === t.name)) {
      const typeInfo = analysisTypes.find((t) => t.id === type);
      setTitle(typeInfo?.name || "New Session");
    }
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      // Validate PDF files
      const invalidFiles = newFiles.filter(
        (file) => file.type !== "application/pdf"
      );

      if (invalidFiles.length > 0) {
        setError(
          `Only PDF files are supported. Invalid files: ${invalidFiles
            .map((f) => f.name)
            .join(", ")}`
        );
        return;
      }

      setFiles((prev) => [...prev, ...newFiles]);
      setError(null);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfFiles = droppedFiles.filter(
      (file) => file.type === "application/pdf"
    );

    if (pdfFiles.length !== droppedFiles.length) {
      setError("Only PDF files are supported");
    } else {
      setFiles((prev) => [...prev, ...pdfFiles]);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCreateSession = async () => {
    if (!title.trim()) {
      setError("Please enter a session title");
      return;
    }

    if (files.length === 0) {
      setError("Please upload at least one PDF file");
      return;
    }

    if (!startingPrompt.trim()) {
      setError("Please enter a starting prompt");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await onCreateSession(
        title,
        selectedType,
        startingPrompt,
        files,
        ragEnabled,
        ragNumChunks,
        ragMinScore
      );
      // Reset and close on success
      setTitle("");
      setStartingPrompt("");
      setFiles([]);
      setSelectedType(AnalysisType.CLINICAL_DECISION_SUPPORT);
      setRagEnabled(true);
      setRagNumChunks(5);
      setRagMinScore(0.3);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create session");
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="new-session-modal-overlay" onClick={onClose}>
      <div className="new-session-modal" onClick={(e) => e.stopPropagation()}>
        <div className="new-session-modal__header">
          <h2>Create New Session</h2>
          <button
            className="new-session-modal__close-btn"
            onClick={onClose}
            disabled={isCreating || isLoadingPrompts}
          >
            ✕
          </button>
        </div>

        {isLoadingPrompts ? (
          <div className="new-session-modal__loading">
            <div className="loading-spinner"></div>
            <p>Loading prompts from server...</p>
          </div>
        ) : (
          <div className="new-session-modal__content">
            {/* Left Side: Analysis Type Selection */}
            <div className="new-session-modal__left">
              <div className="analysis-types-list">
                {analysisTypes.map((type) => (
                  <button
                    key={type.id}
                    className={`analysis-type-item ${
                      selectedType === type.id
                        ? "analysis-type-item--active"
                        : ""
                    }`}
                    onClick={() => handleTypeChange(type.id)}
                    disabled={isCreating}
                  >
                    <span className="analysis-type-item__icon">
                      {type.icon}
                    </span>
                    <div className="analysis-type-item__info">
                      <div className="analysis-type-item__name">
                        {type.name}
                      </div>
                      <div className="analysis-type-item__description">
                        {type.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Side: Configuration */}
            <div className="new-session-modal__right">
              {/* Session Title */}
              <div className="form-group">
                <label htmlFor="session-title">Session Title</label>
                <input
                  id="session-title"
                  type="text"
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter a descriptive title..."
                  disabled={isCreating}
                />
              </div>

              {/* Starting Prompt */}
              <div className="form-group">
                <label htmlFor="starting-prompt">Starting Prompt</label>
                <textarea
                  id="starting-prompt"
                  className="form-control form-control--textarea"
                  value={startingPrompt}
                  onChange={(e) => setStartingPrompt(e.target.value)}
                  rows={8}
                  placeholder="Enter your starting prompt..."
                  disabled={isCreating}
                />
              </div>

              {/* RAG Configuration */}
              <div className="form-group">
                <label className="rag-toggle-label">
                  <input
                    type="checkbox"
                    checked={ragEnabled}
                    onChange={(e) => setRagEnabled(e.target.checked)}
                    disabled={isCreating}
                  />
                  <span>Search in Knowledge Base</span>
                </label>

                {ragEnabled && (
                  <div className="rag-settings">
                    <div className="rag-setting">
                      <label htmlFor="rag-chunks">Chunks to Retrieve</label>
                      <input
                        id="rag-chunks"
                        type="number"
                        min="1"
                        max="10"
                        step="1"
                        value={ragNumChunks}
                        onChange={(e) =>
                          setRagNumChunks(parseInt(e.target.value) || 1)
                        }
                        disabled={isCreating}
                        className="form-control form-control--number"
                      />
                    </div>

                    <div className="rag-setting">
                      <label htmlFor="rag-score">Min Relevance Score (%)</label>
                      <input
                        id="rag-score"
                        type="number"
                        min="0"
                        max="100"
                        step="5"
                        value={Math.round(ragMinScore * 100)}
                        onChange={(e) =>
                          setRagMinScore((parseInt(e.target.value) || 0) / 100)
                        }
                        disabled={isCreating}
                        className="form-control form-control--number"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div className="form-group">
                <label>Upload Documents (PDF only)</label>
                <div
                  className="file-upload-zone"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="application/pdf"
                    onChange={handleFilesChange}
                    style={{ display: "none" }}
                    disabled={isCreating}
                  />
                  <div className="file-upload-zone__content">
                    <span className="file-upload-zone__icon">📄</span>
                    <p>Click to browse or drag and drop PDF files here</p>
                    <span className="file-upload-zone__hint">
                      Multiple files supported
                    </span>
                  </div>
                </div>

                {/* Uploaded Files List */}
                {files.length > 0 && (
                  <div className="uploaded-files-list">
                    {files.map((file, index) => (
                      <div key={index} className="uploaded-file-item">
                        <span className="uploaded-file-item__icon">📄</span>
                        <span className="uploaded-file-item__name">
                          {file.name}
                        </span>
                        <span className="uploaded-file-item__size">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                        <button
                          className="uploaded-file-item__remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                          disabled={isCreating}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-message">
                  <span className="error-message__icon">⚠️</span>
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={onClose}
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreateSession}
                  disabled={isCreating || files.length === 0}
                >
                  {isCreating ? (
                    <>
                      <span className="spinner-small"></span>
                      Creating Session...
                    </>
                  ) : (
                    <>Start Session</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewSessionModal;
